import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Box,
    Typography,
    TextField,
    Button,
    Snackbar,
    Alert,
    CssBaseline,
    Grid,
    IconButton,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    Collapse,
    Divider,
    Autocomplete,
} from "@mui/material";
import { yellow } from "../../../styles/ThemePrimitives";
import dividerImg from "../../../assets/divider.png";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { useUser } from "../../../contexts/UserContext";

const todayISO = () => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
};

const safeText = (v) => (v === null || v === undefined ? "" : String(v));
const toNum = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
};
const formatMoney = (v) =>
    toNum(v).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function StoreOutForm({ onSuccess, editRecord, onClearEdit }) {
    const formRef = useRef(null);
    const snackbarTimerRef = useRef(null);
    const itemSearchDebounceRef = useRef(null);

    const { token } = useUser();
    const base = process.env.REACT_APP_API_BASE || "https://api.fmb52.com/api";

    const [collapsed, setCollapsed] = useState(false);
    const [loadingEdit, setLoadingEdit] = useState(false);
    const [loadingMeta, setLoadingMeta] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    // Header fields
    const [date, setDate] = useState(todayISO());
    const [hijriDate, setHijriDate] = useState("");
    const [miqat, setMiqat] = useState("");
    const [niyazKarnar, setNiyazKarnar] = useState("");
    const [noOfThali, setNoOfThali] = useState("");

    // Row 2 fields
    const [menuText, setMenuText] = useState("");
    const [remarks, setRemarks] = useState("");

    // Cost calculation fields (like image)
    const [nazrulMaqam, setNazrulMaqam] = useState("");
    const [laagat, setLaagat] = useState("");
    const [cooking, setCooking] = useState("");
    const [labour, setLabour] = useState("");
    const [ikram, setIkram] = useState("");
    const [dailyBazaar, setDailyBazaar] = useState("");
    const [storePurchase, setStorePurchase] = useState("");
    const [chicken, setChicken] = useState("");
    const [mutton, setMutton] = useState("");

    // Optional: allow overriding grand total
    const [grandTotalOverride, setGrandTotalOverride] = useState("");

    // Meta
    const [unitOptions, setUnitOptions] = useState([]); // from /store/units
    const [itemSearchResults, setItemSearchResults] = useState([]); // from store-items/items/{search}
    const [itemSearchLoading, setItemSearchLoading] = useState(false);

    // Repeater rows (no dish; no current_stock). row.item = selected item object for display
    const [rows, setRows] = useState([
        { item_id: "", item: null, qty: "", unit: "", rate: "", total: 0 },
    ]);

    // Snackbar (2 sec auto hide)
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

    const showSnackbar = (message, severity = "success") => {
        if (snackbarTimerRef.current) clearTimeout(snackbarTimerRef.current);
        setSnackbar({ open: true, message, severity });
    };

    const handleSnackbarClose = () => {
        if (snackbarTimerRef.current) clearTimeout(snackbarTimerRef.current);
        snackbarTimerRef.current = setTimeout(() => {
            setSnackbar((p) => ({ ...p, open: false }));
        }, 2000);
    };

    useEffect(() => {
        return () => {
            if (snackbarTimerRef.current) clearTimeout(snackbarTimerRef.current);
        };
    }, []);

    const handleCollapseToggle = () => setCollapsed((p) => !p);

    const fetchMeta = async () => {
        if (!token || loadingMeta) return;
        setLoadingMeta(true);

        try {
            const headers = {
                Authorization: `Bearer ${token}`,
                Accept: "application/json",
            };

            const uResp = await fetch(`${base}/store/units`, { method: "GET", headers });
            const uJson = await uResp.json();
            setUnitOptions(Array.isArray(uJson?.data?.units) ? uJson.data.units : []);
        } catch (e) {
            console.error("StoreOut meta fetch failed:", e);
            setUnitOptions([]);
            showSnackbar("Unable to fetch units.", "error");
        } finally {
            setLoadingMeta(false);
        }
    };

    useEffect(() => {
        fetchMeta();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    const fetchItemsSearch = async (search) => {
        if (!token) return;
        setItemSearchLoading(true);
        try {
            const encoded = encodeURIComponent(search || "");
            const url = `${base}/store-items/items/${encoded}`;
            const headers = { Authorization: `Bearer ${token}`, Accept: "application/json" };
            const resp = await fetch(url, { method: "GET", headers });
            if (!resp.ok) {
                setItemSearchResults([]);
                return;
            }
            const json = await resp.json();
            const list = Array.isArray(json?.data) ? json.data : [];
            setItemSearchResults(list);
        } catch (e) {
            console.error("Items search failed:", e);
            setItemSearchResults([]);
        } finally {
            setItemSearchLoading(false);
        }
    };

    const handleItemInputChange = (inputValue) => {
        if (itemSearchDebounceRef.current) clearTimeout(itemSearchDebounceRef.current);
        itemSearchDebounceRef.current = setTimeout(() => {
            fetchItemsSearch(inputValue || "");
        }, 300);
    };

    // Populate form when editing (from record passed from table)
    useEffect(() => {
        const rec = editRecord;
        if (!rec) {
            setLoadingEdit(false);
            return;
        }

        setLoadingEdit(true);
        try {
            const d = rec.date;
            let dateStr = "";
            if (d) {
                try {
                    const dt = new Date(d);
                    dateStr = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
                } catch {}
            }
            setDate(dateStr || todayISO());
            setHijriDate(safeText(rec.hijri_date));
            setMiqat(safeText(rec.miqat));
            setNiyazKarnar(safeText(rec.niyaz_karnar));
            setNoOfThali(rec.no_of_thaali ?? "");
            setMenuText(safeText(rec.menu));
            setNazrulMaqam(rec.nazrul_maqam ?? "");
            setLaagat(rec.laagat ?? "");
            setCooking(rec.cooking ?? "");
            setLabour(rec.labour ?? "");
            setIkram(rec.ikram ?? "");
            setDailyBazaar(rec.daily_bazaar ?? "");
            setStorePurchase(rec.store_purchase ?? "");
            setChicken(rec.chicken ?? "");
            setMutton(rec.mutton ?? "");
            setGrandTotalOverride(rec.grand_total_override ?? "");

            setRemarks(safeText(rec.remarks));

            const itemList = Array.isArray(rec.items) ? rec.items : [];
            const loadedRows = itemList.length
                ? itemList.map((x) => {
                      const qty = toNum(x.qty);
                      const rate = toNum(x.rate);
                      return {
                          id: x.id ?? null,
                          item_id: x.item_id ?? "",
                          item: x.item || null,
                          qty: x.qty ?? "",
                          unit: safeText(x.unit ?? x.item?.unit),
                          rate: x.rate ?? "",
                          total: qty * rate,
                      };
                  })
                : [{ item_id: "", item: null, qty: "", unit: "", rate: "", total: 0 }];
            setRows(loadedRows);
            setCollapsed(false);
        } catch (e) {
            console.error("Populate form for edit:", e);
            if (onClearEdit) onClearEdit();
        } finally {
            setLoadingEdit(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editRecord]);

    const findItem = (id) => {
        const nid = Number(id);
        if (!nid) return null;
        return itemSearchResults.find((x) => Number(x?.id) === nid) || null;
    };

    const recalcRow = (r) => {
        const qty = toNum(r.qty);
        const rate = toNum(r.rate);
        const total = qty * rate;
        return { ...r, total };
    };

    const updateRow = (idx, patch) => {
        setRows((prev) => {
            const copy = prev.map((r, i) => (i === idx ? { ...r, ...patch } : r));
            copy[idx] = recalcRow(copy[idx]);
            return copy;
        });
    };

    const fetchLastRate = async (itemId) => {
        if (!itemId || !token) return null;
        try {
            const headers = { Authorization: `Bearer ${token}`, Accept: "application/json" };
            const resp = await fetch(`${base}/store-items/${itemId}/last-rate`, { method: "GET", headers });
            if (!resp.ok) return null;
            const json = await resp.json();
            const data = json?.data ?? json;
            return data;
        } catch (e) {
            console.error("Last rate fetch error:", e);
            return null;
        }
    };

    const onSelectItem = async (idx, selectedItem) => {
        const itemId = selectedItem?.id ?? "";
        const lastRateData = await fetchLastRate(itemId);
        const rate = lastRateData?.rate ?? selectedItem?.rate ?? "";
        const unit = lastRateData?.unit ?? selectedItem?.unit ?? "";
        updateRow(idx, {
            item_id: itemId,
            item: selectedItem || null,
            unit: safeText(unit),
            rate: rate !== "" && rate !== undefined && rate !== null ? String(rate) : "",
        });
    };

    const addRow = () => {
        setRows((p) => [...p, { item_id: "", item: null, qty: "", unit: "", rate: "", total: 0 }]);
    };

    const removeRow = (idx) => {
        setRows((prev) => {
            const copy = [...prev];
            copy.splice(idx, 1);
            return copy.length ? copy : [{ item_id: "", item: null, qty: "", unit: "", rate: "", total: 0 }];
        });
    };

    // ✅ Gross Total = sum of item totals
    const grossTotal = useMemo(() => rows.reduce((sum, r) => sum + toNum(r.total), 0), [rows]);

    // ✅ Other charges total
    const otherChargesTotal = useMemo(() => {
        return (
            toNum(nazrulMaqam) +
            toNum(laagat) +
            toNum(cooking) +
            toNum(labour) +
            toNum(ikram) +
            toNum(dailyBazaar) +
            toNum(storePurchase) +
            toNum(chicken) +
            toNum(mutton)
        );
    }, [nazrulMaqam, laagat, cooking, labour, ikram, dailyBazaar, storePurchase, chicken, mutton]);

    // ✅ Grand Total = gross + charges (auto, but editable)
    const grandTotalAuto = useMemo(() => grossTotal + otherChargesTotal, [grossTotal, otherChargesTotal]);
    const grandTotalShown = grandTotalOverride !== "" ? toNum(grandTotalOverride) : grandTotalAuto;

    const validate = () => {
        if (!date) return "Date is required.";
        if (!niyazKarnar.trim()) return "Name is required.";
        if (!miqat.trim()) return "Miqat is required.";
        if (!(toNum(noOfThali) > 0)) return "No of Thali must be greater than 0.";

        const cleaned = rows
            .filter((r) => r.item_id)
            .map((r) => ({
                item_id: Number(r.item_id),
                qty: toNum(r.qty),
                rate: toNum(r.rate),
            }))
            .filter((x) => x.item_id && x.qty > 0);

        if (cleaned.length === 0) return "Please add at least 1 item row with Item and Qty > 0.";

        return null;
    };

    const resetForm = () => {
        setDate(todayISO());
        setHijriDate("");
        setMiqat("");
        setNiyazKarnar("");
        setNoOfThali("");
        setMenuText("");
        setRemarks("");

        setNazrulMaqam("");
        setLaagat("");
        setCooking("");
        setLabour("");
        setIkram("");
        setDailyBazaar("");
        setStorePurchase("");
        setChicken("");
        setMutton("");
        setGrandTotalOverride("");

        setRows([{ item_id: "", item: null, qty: "", unit: "", rate: "", total: 0 }]);
    };

    const handleSubmit = async () => {
        if (!token) return;

        const err = validate();
        if (err) return showSnackbar(err, "error");

        setSubmitting(true);
        try {
            const payload = {
                date,
                hijri_date: hijriDate.trim() || null,
                niyaz_karnar: niyazKarnar.trim(),
                miqat: miqat.trim(),
                no_of_thaali: toNum(noOfThali),
                menu: menuText.trim() || null,
                remarks: remarks.trim() || null,

                nazrul_maqam: toNum(nazrulMaqam),
                laagat: toNum(laagat),
                cooking: toNum(cooking),
                labour: toNum(labour),
                ikram: toNum(ikram),
                daily_bazaar: toNum(dailyBazaar),
                store_purchase: toNum(storePurchase),
                chicken: toNum(chicken),
                mutton: toNum(mutton),

                items: rows
                    .filter((r) => r.item_id)
                    .map((r) => ({
                        ...(r.id ? { id: Number(r.id) } : {}),
                        dish_id: null,
                        item_id: Number(r.item_id),
                        unit: safeText(r.unit).trim() || null,
                        qty: toNum(r.qty),
                        rate: toNum(r.rate),
                    })),
            };

            const url = editRecord?.id ? `${base}/store-out/${editRecord.id}` : `${base}/store-out/create`;
            const resp = await fetch(url, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                    Accept: "application/json",
                },
                body: JSON.stringify(payload),
            });

            const data = await resp.json();

            if (resp.ok && (data?.success === true || data?.code === 200)) {
                showSnackbar(data?.message || (editRecord?.id ? "Store out updated" : "StoreOut saved"), "success");
                resetForm();
                if (onClearEdit) onClearEdit();
                if (onSuccess) onSuccess();
                window.scrollTo({ top: 0, behavior: "smooth" });
            } else {
                showSnackbar(data?.message || "Submission failed.", "error");
            }
        } catch (e) {
            console.error("StoreOut submit error:", e);
            showSnackbar("Error occurred during submission.", "error");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <CssBaseline />

            <Box
                ref={formRef}
                sx={{
                    pt: 2,
                    pb: 3,
                    pl: 3,
                    pr: 3,
                    mr: 2,
                    ml: 2,
                    mb: 1,
                    backgroundColor: "#fff",
                    border: "1px solid #F4EBD0",
                    borderRadius: 2,
                    boxShadow: 1,
                }}
            >
                {/* Header */}
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Typography
                        variant="h6"
                        sx={{ fontWeight: "bold", marginBottom: 1, padding: "8px 16px", borderRadius: 1 }}
                    >
                        Store Out
                    </Typography>

                    <IconButton
                        onClick={handleCollapseToggle}
                        sx={{ color: yellow[300], "&:hover": { color: yellow[400] } }}
                    >
                        {collapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
                    </IconButton>
                </Box>

                {!collapsed && (
                    <Box
                        sx={{
                            width: "calc(100% + 48px)",
                            position: "relative",
                            height: { xs: 10, sm: 15 },
                            backgroundImage: `url(${dividerImg})`,
                            backgroundSize: "contain",
                            backgroundRepeat: "repeat-x",
                            backgroundPosition: "center",
                            mb: 2,
                            marginLeft: "-24px",
                            marginRight: "-24px",
                        }}
                    />
                )}

                <Collapse in={!collapsed}>
                    <Grid container spacing={2} alignItems="center" sx={{ pr: 2 }}>
                        {/* Row 1 */}
                        <Grid item xs={12} md={3}>
                            <TextField
                                fullWidth
                                type="date"
                                label="Date"
                                value={date}
                                onChange={(e) => setDate(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>

                        <Grid item xs={12} md={3}>
                            <TextField
                                fullWidth
                                label="Hijri Date"
                                value={hijriDate}
                                onChange={(e) => setHijriDate(e.target.value)}
                                placeholder="e.g. 1446-01-15"
                            />
                        </Grid>

                        <Grid item xs={12} md={3}>
                            <TextField
                                fullWidth
                                label="Name"
                                value={niyazKarnar}
                                onChange={(e) => setNiyazKarnar(e.target.value)}
                            />
                        </Grid>

                        <Grid item xs={12} md={3}>
                            <TextField
                                fullWidth
                                type="number"
                                label="No of Thali"
                                value={noOfThali}
                                onChange={(e) => setNoOfThali(e.target.value)}
                                inputProps={{ min: 1, step: 1 }}
                            />
                        </Grid>

                        {/* Row 2 */}
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Menu"
                                value={menuText}
                                onChange={(e) => setMenuText(e.target.value)}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Miqat"
                                value={miqat}
                                onChange={(e) => setMiqat(e.target.value)}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Remarks"
                                inputProps={{ maxLength: 50 }}
                                value={remarks}
                                onChange={(e) => setRemarks(e.target.value)}
                            />
                        </Grid>

                        {/* Repeater */}
                        <Grid item xs={12}>
                            <Box sx={{ border: "1px dashed rgba(0,0,0,0.18)", borderRadius: 2, p: 2, mt: 1 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                                    Items Used
                                </Typography>

                                {rows.map((r, idx) => (
                                    <Grid
                                        key={idx}
                                        container
                                        spacing={2}
                                        alignItems="center"
                                        sx={{
                                            mb: 1.2,
                                            pb: 1.2,
                                            borderBottom: idx === rows.length - 1 ? "none" : "1px solid rgba(0,0,0,0.06)",
                                        }}
                                    >
                                        {/* Item - filled from store-items/items/{search} */}
                                        <Grid item xs={12} md={3}>
                                            <Autocomplete
                                                size="small"
                                                disabled={loadingMeta}
                                                options={itemSearchResults}
                                                value={r.item || null}
                                                loading={itemSearchLoading}
                                                getOptionLabel={(opt) =>
                                                    opt?.name
                                                        ? `${opt.name}${opt.category ? ` (${opt.category})` : ""}`
                                                        : ""
                                                }
                                                onChange={(_, newVal) => onSelectItem(idx, newVal)}
                                                onInputChange={(_, inputValue) => handleItemInputChange(inputValue)}
                                                onOpen={() => {
                                                    if (itemSearchResults.length === 0) fetchItemsSearch("");
                                                }}
                                                renderInput={(params) => (
                                                    <TextField {...params} label="Item" placeholder="Type to search..." />
                                                )}
                                                renderOption={(props, opt) => (
                                                    <li {...props} key={opt.id}>
                                                        <Box>
                                                            <Typography variant="body2" fontWeight={600}>
                                                                {opt.name}
                                                            </Typography>
                                                            {opt.category && (
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {opt.category}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    </li>
                                                )}
                                                isOptionEqualToValue={(opt, val) => Number(opt?.id) === Number(val?.id)}
                                            />
                                        </Grid>

                                        {/* Qty */}
                                        <Grid item xs={12} sm={6} md={1.2}>
                                            <TextField
                                                fullWidth
                                                type="number"
                                                label="Qty"
                                                value={safeText(r.qty)}
                                                onChange={(e) => updateRow(idx, { qty: e.target.value })}
                                                inputProps={{ min: 1, step: "any" }}
                                            />
                                        </Grid>

                                        {/* Unit */}
                                        <Grid item xs={12} sm={6} md={1.3}>
                                            <FormControl fullWidth size="small" disabled={loadingMeta}>
                                                <InputLabel id={`unit-${idx}`}>Unit</InputLabel>
                                                <Select
                                                    labelId={`unit-${idx}`}
                                                    value={safeText(r.unit)}
                                                    label="Unit"
                                                    onChange={(e) => updateRow(idx, { unit: e.target.value })}
                                                >
                                                    <MenuItem value="">
                                                        <em>Select</em>
                                                    </MenuItem>
                                                    {unitOptions.map((u) => (
                                                        <MenuItem key={u} value={u}>
                                                            {u}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>

                                        {/* Rate */}
                                        <Grid item xs={12} sm={6} md={1.3}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                type="number"
                                                label="Rate"
                                                value={safeText(r.rate)}
                                                onChange={(e) => updateRow(idx, { rate: e.target.value })}
                                                inputProps={{ min: 0, step: "any" }}
                                            />
                                        </Grid>

                                        {/* Total */}
                                        <Grid item xs={12} sm={6} md={1.3}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Total"
                                                value={formatMoney(r.total)}
                                                InputProps={{ readOnly: true }}
                                            />
                                        </Grid>

                                        {/* Remove */}
                                        <Grid item xs={12} sm={6} md={0.7}>
                                            <IconButton
                                                onClick={() => removeRow(idx)}
                                                disabled={rows.length === 1}
                                                title="Remove"
                                                sx={{ color: "error.main" }}
                                            >
                                                <DeleteOutlineOutlinedIcon />
                                            </IconButton>
                                        </Grid>
                                    </Grid>
                                ))}

                                <Divider sx={{ my: 1.5 }} />

                                <Button
                                    variant="outlined"
                                    onClick={addRow}
                                    startIcon={<AddCircleOutlineIcon />}
                                    sx={{ borderRadius: 2 }}
                                >
                                    Add Row
                                </Button>
                            </Box>
                        </Grid>

                        {/* Calculation section – single column (right) */}
                        <Grid item xs={12}>
                            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
                                <Box sx={{ border: "1px solid rgba(0,0,0,0.08)", borderRadius: 2, p: 1.5, width: "100%", maxWidth: 320 }}>
                                    <TextField fullWidth size="small" label="Gross Total" value={formatMoney(grossTotal)} InputProps={{ readOnly: true }} sx={{ mb: 1 }} />
                                    <TextField fullWidth size="small" label="Nazrul Maqam A.S." type="number" value={nazrulMaqam} onChange={(e) => setNazrulMaqam(e.target.value)} sx={{ mb: 1 }} />
                                    <TextField fullWidth size="small" label="Laagat / Electricity / Crockery" type="number" value={laagat} onChange={(e) => setLaagat(e.target.value)} sx={{ mb: 1 }} />
                                    <TextField fullWidth size="small" label="Cooking Charges" type="number" value={cooking} onChange={(e) => setCooking(e.target.value)} sx={{ mb: 1 }} />
                                    <TextField fullWidth size="small" label="Labour Expense" type="number" value={labour} onChange={(e) => setLabour(e.target.value)} sx={{ mb: 1 }} />
                                    <TextField fullWidth size="small" label="Ikram" type="number" value={ikram} onChange={(e) => setIkram(e.target.value)} sx={{ mb: 1 }} />
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Grand Total"
                                        type="number"
                                        value={grandTotalOverride !== "" ? grandTotalOverride : String(grandTotalAuto)}
                                        onChange={(e) => setGrandTotalOverride(e.target.value)}
                                        helperText="Auto calculated, but editable"
                                        sx={{ mb: 1, backgroundColor: "rgba(0,0,0,0.04)", borderRadius: 1 }}
                                    />
                                </Box>
                            </Box>
                        </Grid>

                        {/* Submit / Update */}
                        <Grid item xs={12} sx={{ textAlign: "right", mt: 1 }}>
                            <Button
                                variant="contained"
                                onClick={handleSubmit}
                                disabled={submitting || loadingEdit}
                                sx={{
                                    color: "white",
                                    backgroundColor: yellow[300],
                                    "&:hover": { backgroundColor: yellow[200], color: "#000" },
                                    minWidth: 160,
                                }}
                            >
                                {submitting ? <CircularProgress size={20} color="inherit" /> : editRecord?.id ? "Update" : "Submit"}
                            </Button>
                        </Grid>
                    </Grid>
                </Collapse>
            </Box>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={2000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert onClose={handleSnackbarClose} severity={snackbar.severity} variant="filled" sx={{ width: "100%" }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
}
